(ns practitest.frontend.integration-settings.integration-field-mapping.events
  (:require [re-frame.core :as re-frame]
            [ajax.core :as ajax]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.components.notifications :as notifications]))

(def db-key :integration-field-mapping)

(defn- empty-row []
  { :ex-field-id    ""
    :ex-field-name  ""
    :ex-field-path  ""
    :map-to         "none"
    :pt-field-type  ""
    :for-issues     false
    :for-requirements false
    :fail-and-issue false})

(re-frame/reg-event-db
  ::initialize-db
  (fn [db [_ options]]
    (let [rows (vec (:rows options []))]
      (assoc db db-key
             { :rows             rows
               :saved-rows       rows
               :ex-fields        (vec (:ex-fields options []))
               :pt-fields        (vec (:pt-fields options []))
               :valid-pairs      (vec (:valid-pairs options []))
               :save-url         (:save-url options "")
               :payload-key      (:payload-key options "")
               :bugtracker-name  (:bugtracker-name options "")
               :saving?          false
               :changes-made?    false}))))

(re-frame/reg-event-db
  ::add-row
  (fn [db _]
    (-> db
        (update-in [db-key :rows] conj (empty-row))
        (assoc-in [db-key :changes-made?] true))))

(re-frame/reg-event-db
  ::remove-row
  (fn [db [_ idx]]
    (let [rows (get-in db [db-key :rows])
          new-rows (vec (concat (subvec rows 0 idx) (subvec rows (inc idx))))]
      (-> db
          (assoc-in [db-key :rows] new-rows)
          (assoc-in [db-key :changes-made?] true)))))

(re-frame/reg-event-db
  ::set-row-field
  (fn [db [_ idx field value]]
    (-> db
        (assoc-in [db-key :rows idx field] value)
        (assoc-in [db-key :changes-made?] true))))

(defn entity-blocked? [map-to entities entity]
  (and (not= map-to "description")
       (or (= map-to "none") (= map-to "")
           (not (some #(= % entity) entities)))))

(re-frame/reg-event-db
  ::set-external-field
  (fn [db [_ idx ex-field-id]]
    (let [ex-fields (get-in db [db-key :ex-fields] [])
          field     (first (filter #(= (:id %) ex-field-id) ex-fields))
          ex-name   (:name field "")
          ex-path   (:path field "")]
      (-> db
          (assoc-in [db-key :rows idx :ex-field-id] ex-field-id)
          (assoc-in [db-key :rows idx :ex-field-name] ex-name)
          (assoc-in [db-key :rows idx :ex-field-path] ex-path)
          (assoc-in [db-key :rows idx :map-to] "none")
          (assoc-in [db-key :rows idx :pt-field-type] "")
          (assoc-in [db-key :rows idx :for-issues] false)
          (assoc-in [db-key :rows idx :for-requirements] false)
          (assoc-in [db-key :changes-made?] true)))))

(re-frame/reg-event-db
  ::set-map-to
  (fn [db [_ idx value]]
    (let [pt-fields  (get-in db [db-key :pt-fields] [])
          pt-field   (first (filter #(= (:id %) value) pt-fields))
          pt-type    (if pt-field (:type pt-field "") "")
          entities   (if pt-field (get pt-field :entities []) [])]
      (cond-> (-> db
                  (assoc-in [db-key :rows idx :map-to] value)
                  (assoc-in [db-key :rows idx :pt-field-type] pt-type)
                  (assoc-in [db-key :changes-made?] true))
        (entity-blocked? value entities "Issue")        (assoc-in [db-key :rows idx :for-issues] false)
        (entity-blocked? value entities "Requirement")  (assoc-in [db-key :rows idx :for-requirements] false)))))

(defn- build-row-payload [idx row]
  (let [map-to       (:map-to row)
        is-pt-field? (and (not= map-to "none") (not= map-to "description") (not= map-to ""))
        base         { "ex_field_name" (:ex-field-name row "")
                       "ex_field_path"  (:ex-field-path row "")
                       "pt_field_id"     (if is-pt-field? map-to "")
                       "pt_field_type"   (:pt-field-type row "")
                       "sync_to"         { (str idx) map-to}}]
    (cond-> base
      (:for-issues row)       (assoc "issue_checkbox" "issue_checked")
      (:for-requirements row) (assoc "requirement_checkbox" "req_checked")
      (:fail-and-issue row)   (assoc "show_in_modal_checkbox" "show_in_modal_checked"))))

(defn- validate-rows [rows pt-fields ex-fields]
  (let [pt-field-ids (set (map :id pt-fields))
        ex-field-ids (set (map :id ex-fields))]
    (vec (distinct
      (reduce
        (fn [errors row]
          (cond
            (or (= (:ex-field-id row) "")
                (not (contains? ex-field-ids (:ex-field-id row))))
            (conj errors (get-translation "integration-field-mapping.validation.select-external-field"
                                          "Please select an external field for each row"))

            (and (not= (:map-to row) "description")
                 (not= (:map-to row) "")
                 (not (contains? pt-field-ids (:map-to row))))
            (conj errors (get-translation "integration-field-mapping.validation.select-pt-field"
                                          "Please select a PractiTest field for each row"))

            (and (not (:for-issues row)) (not (:for-requirements row)))
            (conj errors (get-translation "integration-field-mapping.validation.select-entity"
                                          "Please select at least one entity (Issues or Requirements) for each row"))

            :else errors))
        []
        rows)))))

(re-frame/reg-event-fx
  ::save
  (fn [{:keys [db]} _]
    (let [state       (get db db-key)
          rows        (:rows state [])
          save-url    (:save-url state "")
          payload-key (:payload-key state "")
          errors      (validate-rows rows
                                     (get-in state [:pt-fields] [])
                                     (get-in state [:ex-fields] []))]
      (if (seq errors)
        {:fx (mapv (fn [err] [:dispatch [::notifications/error2 err]]) errors)}
        {:db         (assoc-in db [db-key :saving?] true)
         :http-xhrio { :method          :post
                       :uri             save-url
                       :params          { payload-key (map-indexed build-row-payload rows)}
                       :format          (ajax/json-request-format)
                       :response-format (ajax/json-response-format { :keywords? true})
                       :on-success      [::save-success]
                       :on-failure      [::save-failure]}}))))

(re-frame/reg-event-fx
  ::save-success
  (fn [{:keys [db]} _]
    {:db (-> db
             (assoc-in [db-key :saving?] false)
             (assoc-in [db-key :saved-rows] (get-in db [db-key :rows]))
             (assoc-in [db-key :changes-made?] false))
     :fx [[:dispatch [::notifications/success2
                      (get-translation "integration-field-mapping.updated-successfully"
                                       "Settings updated successfully")]]]}))

(re-frame/reg-event-fx
  ::save-failure
  (fn [{:keys [db]} _]
    {:db (assoc-in db [db-key :saving?] false)
     :fx [[:dispatch [::notifications/error2
                      (get-translation "integration-field-mapping.save-failed"
                                       "Failed to save settings")]]]}))

(re-frame/reg-event-db
  ::cancel
  (fn [db _]
    (-> db
        (assoc-in [db-key :rows] (get-in db [db-key :saved-rows]))
        (assoc-in [db-key :changes-made?] false))))
